import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';

let initialized = false;
const DEFAULT_SENTRY_TUNNEL = '/sentry-tunnel';
const DSN_PATTERN = /https?:\/\/[^/\s]+@[^/\s]+/gi;
const SENSITIVE_QUERY_KEYS = new Set(['email', 'nip', 'pesel']);

type TagListEntry = [unknown, unknown] | { key?: unknown; value?: unknown };
type ExceptionValueLike = {
  stacktrace?: {
    frames?: unknown;
  };
  type?: unknown;
  value?: unknown;
};
type LandingSentryEvent = {
  breadcrumbs?: Array<{ data?: { url?: unknown } }>;
  environment?: unknown;
  exception?: {
    values?: unknown;
  };
  extra?: unknown;
  logentry?: {
    message?: unknown;
  };
  message?: unknown;
  request?: {
    url?: unknown;
  };
  tags?: Record<string, unknown> | TagListEntry[];
  transaction?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getTagValue(event: LandingSentryEvent, key: string): string {
  const tags = event?.tags;
  if (!tags) return '';
  if (Array.isArray(tags)) {
    for (const item of tags) {
      if (Array.isArray(item) && item[0] === key) {
        return String(item[1] ?? '');
      }
      if (isRecord(item) && item.key === key) {
        return String(item.value ?? '');
      }
    }
    return '';
  }
  if (isRecord(tags)) {
    return String(tags[key] ?? '');
  }
  return '';
}

function asExceptionValue(value: unknown): ExceptionValueLike | null {
  return isRecord(value) ? value : null;
}

function hasStackFrames(event: LandingSentryEvent): boolean {
  const values = event?.exception?.values;
  if (!Array.isArray(values)) return false;
  return values.some((value) => {
    const exceptionValue = asExceptionValue(value);
    const frames = exceptionValue?.stacktrace?.frames;
    return Array.isArray(frames) && frames.length > 0;
  });
}

function serializeExtraForText(value: unknown): string {
  if (!value) return '';
  const seen = new WeakSet<object>();
  try {
    return (
      JSON.stringify(value, (_key, item: unknown) => {
        if (typeof item === 'bigint') return item.toString();
        if (isRecord(item)) {
          if (seen.has(item)) return '[Circular]';
          seen.add(item);
        }
        return item;
      }) ?? ''
    );
  } catch {
    return '';
  }
}

function collectEventText(event: LandingSentryEvent): string {
  const exceptionValues = Array.isArray(event?.exception?.values) ? event.exception.values : [];
  const exceptionText = exceptionValues
    .map((value) => {
      const exceptionValue = asExceptionValue(value);
      return [exceptionValue?.type ?? '', exceptionValue?.value ?? ''].join(' ');
    })
    .join('\n');
  return [
    event?.message,
    event?.logentry?.message,
    exceptionText,
    serializeExtraForText(event?.extra),
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

function eventSurfaces(event: LandingSentryEvent): string[] {
  const surfaces: string[] = [];
  for (const value of [
    event?.request?.url,
    event?.transaction,
    getTagValue(event, 'url'),
    getTagValue(event, 'transaction'),
  ]) {
    if (typeof value === 'string' && value.length > 0) {
      surfaces.push(value.toLowerCase());
    }
  }
  return surfaces;
}

function scrubBreadcrumbUrl(value: string): string {
  try {
    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
    const parsed = new URL(value, 'https://bookeper.local');
    for (const key of Array.from(parsed.searchParams.keys())) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        parsed.searchParams.delete(key);
      }
    }
    if (isAbsolute) {
      return parsed.toString();
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return value
      .replace(/[?&](email|nip|pesel)=[^&]*/gi, '')
      .replace(/\?&/, '?')
      .replace(/\?$/, '')
      .replace(/\/&/, '/?');
  }
}

function isProductionEvent(event: LandingSentryEvent): boolean {
  return (
    String(event?.environment ?? getTagValue(event, 'environment') ?? '').toLowerCase() ===
    'production'
  );
}

export function shouldDropLandingSentryEvent(event: LandingSentryEvent): boolean {
  if (!isProductionEvent(event)) return false;
  if (hasStackFrames(event)) return false;
  const text = collectEventText(event);
  if (!/runtime\.sendmessage/.test(text) || !/tab not found/.test(text)) {
    return false;
  }
  return eventSurfaces(event).some((surface) => {
    try {
      return new URL(surface).hostname.endsWith('bookeper.pl');
    } catch {
      return surface.startsWith('/') || surface.includes('bookeper.pl');
    }
  });
}

// BOO-3933 / GH#6443 / Sentry BOOKEPER-1CR: "Error invoking postMessage: Java object
// is gone", 1 event, userCount 0. Fetched the full event from Sentry — every occurrence
// is `browser: Instagram <version>` on Android, `request.url` on `www.bookeper.pl` with
// `referer: https://l.instagram.com/` + `utm_source=ig`/`fbclid` query params (a link
// clicked from Instagram, opened in Instagram's embedded in-app-browser WebView), and
// the throwing stack frames are ALL `iabjs://navigation_performance_logger_android`
// (`sendBeforeUnloadMessage` / `sendDataToNative`) — Meta's OWN page-performance
// telemetry bridge script, injected into the page by the host app, notifying its native
// Android counterpart on navigation/unload. "Java object is gone" is that native-side
// bridge object having already been torn down (WebView recycled / activity finished)
// by the time this async callback fires — a well-documented race INSIDE Meta's in-app
// browser SDK, unrelated to any Bookeper code (the only non-iabjs frame is our own
// Sentry SDK's `addEventListener` wrapper, which is how the mechanism is
// `auto.browser.browserapierrors.addEventListener` rather than a direct capture).
const IN_APP_BROWSER_BRIDGE_SCHEME = 'iabjs://';
const IN_APP_BROWSER_BRIDGE_MESSAGE_PATTERN = /(error invoking postmessage|java object is gone)/;

function hasInAppBrowserBridgeStackFrame(event: LandingSentryEvent): boolean {
  const values = event?.exception?.values;
  if (!Array.isArray(values)) return false;
  return values.some((value) => {
    const exceptionValue = asExceptionValue(value);
    const frames = exceptionValue?.stacktrace?.frames;
    if (!Array.isArray(frames)) return false;
    return frames.some((frame) => {
      if (!isRecord(frame)) return false;
      const filename = frame.filename;
      return typeof filename === 'string' && filename.startsWith(IN_APP_BROWSER_BRIDGE_SCHEME);
    });
  });
}

export function isInAppBrowserBridgeNoise(event: LandingSentryEvent): boolean {
  if (!isProductionEvent(event)) return false;
  if (!IN_APP_BROWSER_BRIDGE_MESSAGE_PATTERN.test(collectEventText(event))) return false;
  return hasInAppBrowserBridgeStackFrame(event);
}

export function createLandingBeforeSend() {
  return function beforeSend(event: ErrorEvent): ErrorEvent | null {
    if (shouldDropLandingSentryEvent(event)) {
      return null;
    }

    if (isInAppBrowserBridgeNoise(event)) {
      return null;
    }

    // Strip PII from URLs in breadcrumbs
    if (event.breadcrumbs) {
      for (const crumb of event.breadcrumbs) {
        const breadcrumbUrl = crumb.data?.url;
        if (typeof breadcrumbUrl === 'string') {
          crumb.data.url = scrubBreadcrumbUrl(breadcrumbUrl);
        }
      }
    }
    return event;
  };
}

/**
 * Initialize Sentry for the landing app (www.bookeper.pl).
 *
 * Uses VITE_SENTRY_DSN env var. Shares the frontend Sentry project
 * — errors are distinguishable by URL (www.bookeper.pl vs app.bookeper.pl).
 */
export function initSentry() {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] VITE_SENTRY_DSN not set; skipping init');
    }
    return;
  }

  try {
    const deploymentEnv =
      import.meta.env.VITE_VERCEL_ENV || import.meta.env.VITE_APP_ENV || import.meta.env.MODE;
    const tunnel = /(^|\.)bookeper\.pl$/i.test(window.location.hostname)
      ? import.meta.env.VITE_SENTRY_TUNNEL || DEFAULT_SENTRY_TUNNEL
      : undefined;

    Sentry.init({
      dsn,
      environment: deploymentEnv,
      tunnel,
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 0.0,
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.0,
      sendDefaultPii: false,
      integrations: [Sentry.browserTracingIntegration()],
      beforeSend: createLandingBeforeSend(),
    });

    initialized = true;
  } catch (error) {
    const sanitizedMessage =
      error instanceof Error ? error.message.replace(DSN_PATTERN, '[redacted-dsn]') : undefined;
    if (sanitizedMessage) {
      console.warn('[Sentry] Failed to initialize', sanitizedMessage);
      return;
    }
    console.warn('[Sentry] Failed to initialize');
  }
}

export { Sentry };
